import { PYTHON_DIRECTORIES } from '../services/python/interfaces/python.interface';

/**
 * PythonUtils - Shared utilities for Python operations
 *
 * This helper provides common Python utilities that are used across
 * multiple Python services to eliminate code duplication.
 */
export class PythonUtils {

  /**
   * Get the Python code for recursive JSON parsing function
   * This function handles nested JSON strings and complex data structures
   * @returns string - Complete Python function code
   */
  static getRecursiveJsonLoadsCode(): string {
    return `
def recursive_json_loads(obj):
    if isinstance(obj, str):
        try:
            parsed = json.loads(obj)
            if isinstance(parsed, (dict, list)):
                return recursive_json_loads(parsed)
            return parsed
        except (json.JSONDecodeError, TypeError):
            return obj
    elif isinstance(obj, dict):
        return {k: recursive_json_loads(v) for k, v in obj.items()}
    elif isinstance(obj, list):
        return [recursive_json_loads(item) for item in obj]
    else:
        return obj
`;
  }

  /**
   * Sanitize module name to be Python-compatible
   * Removes special characters and converts to lowercase with underscores
   * @param name - Original module name
   * @returns string - Sanitized module name
   */
  static sanitizeModuleName(name: string): string {
    if (!name) {
      return 'unnamed_module';
    }

    return name
      .replace(/[^a-zA-Z0-9_]/g, '_')  // Replace non-alphanumeric with underscore
      .replace(/^[0-9]/, '_$&')        // Prefix with underscore if starts with number
      .toLowerCase()                   // Convert to lowercase
      .replace(/_+/g, '_')             // Replace multiple underscores with single
      .replace(/(?:^_|_$)/g, '');      // Remove leading/trailing underscores (explicit grouping)
  }

  /**
   * Create complete Python code for setting data variable with recursive JSON parsing
   * @param data - Data to set as 'data' variable in Python
   * @returns string - Complete Python code
   */
  static createDataVariableCode(data: any): string {
    return `
import json

${this.getRecursiveJsonLoadsCode()}

globals()['data'] = recursive_json_loads(${JSON.stringify(JSON.stringify(data))})
'data_set'
`;
  }

  /**
   * Create Python import statement for JSON
   * @returns string - JSON import code
   */
  static getJsonImportCode(): string {
    return 'import json\n';
  }

  /**
   * Validate Python variable name
   * @param name - Variable name to validate
   * @returns boolean - True if valid Python variable name
   */
  static isValidPythonVariableName(name: string): boolean {
    // Python variable name rules: start with letter or underscore,
    // followed by letters, digits, or underscores
    const pythonVarRegex = /^[a-zA-Z_][a-zA-Z0-9_]*$/;

    // Also check against Python keywords
    const pythonKeywords = [
      'and', 'as', 'assert', 'break', 'class', 'continue', 'def', 'del',
      'elif', 'else', 'except', 'exec', 'finally', 'for', 'from', 'global',
      'if', 'import', 'in', 'is', 'lambda', 'not', 'or', 'pass', 'print',
      'raise', 'return', 'try', 'while', 'with', 'yield', 'True', 'False', 'None'
    ];

    return pythonVarRegex.test(name) && !pythonKeywords.includes(name);
  }

  /**
   * Escape string for safe use in Python code
   * @param str - String to escape
   * @returns string - Escaped string
   */
  static escapePythonString(str: string): string {
    return str
      .replace(/\\/g, '\\\\')  // Escape backslashes
      .replace(/'/g, "\\'")    // Escape single quotes
      .replace(/"/g, '\\"')    // Escape double quotes
      .replace(/\n/g, '\\n')   // Escape newlines
      .replace(/\r/g, '\\r')   // Escape carriage returns
      .replace(/\t/g, '\\t');  // Escape tabs
  }

  /**
   * Normalize file path to absolute form
   *
   * Ensures consistent path format for journal lookups across all components
   * Used by FileSystemProxy, PyodideFileJournal, and PythonFileSystemHelper
   *
   * @param path - Path to normalize (can be relative or absolute)
   * @param workingDir - Optional working directory (defaults to PYTHON_DIRECTORIES.WORKING_DIR)
   * @returns string - Normalized absolute path
   *
   * Examples:
   *   './data.csv' → '/home/pyodide/data.csv'
   *   'data.csv' → '/home/pyodide/data.csv'
   *   '/home/pyodide/data.csv' → '/home/pyodide/data.csv'
   */
  static normalizePath(path: string, workingDir?: string): string {
    // Remove redundant ./ at start
    if (path.startsWith('./')) {
      path = path.substring(2);
    }

    // Remove multiple slashes
    path = path.replace(/\/+/g, '/');

    // Convert relative to absolute
    if (!path.startsWith('/')) {
      const wd = workingDir || PYTHON_DIRECTORIES.WORKING_DIR;
      path = `${wd}/${path}`;
    }

    // Resolve . and .. path components (SECURITY CRITICAL - PROT-810)
    // This prevents path traversal attacks like: ./files/../project/hack.py
    const parts = path.split('/').filter(Boolean);
    const resolved: string[] = [];

    for (const part of parts) {
      if (part === '..') {
        // Go up one directory (if not at root)
        if (resolved.length > 0) {
          resolved.pop();
        }
        // Ignore .. at root level (can't go above /)
      } else if (part !== '.') {
        // Skip current directory markers, add everything else
        resolved.push(part);
      }
    }

    path = '/' + resolved.join('/');

    // Remove trailing slash (except for root)
    if (path.endsWith('/') && path.length > 1) {
      path = path.slice(0, -1);
    }

    return path;
  }
}
